import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";
import SeoMarketingShell from "../../components/seo/SeoMarketingShell";

const faqItems = [
  {
    q: "Do Integrations Require Engineering Time?",
    a: "Usually very little. Bellhopt handles setup guidance, property mapping, and rollout validation with your team.",
  },
  {
    q: "What Data Syncs Into Bellhopt?",
    a: "Bellhopt syncs reservation timing, property details, access and entry notes, and the data needed to power guest-facing pre-arrival flows.",
  },
  {
    q: "Will New Properties Be Added Automatically?",
    a: "Yes. When supported PMS data is available, Bellhopt can create or update newly added properties so your portfolio stays aligned.",
  },
  {
    q: "Do Integrations Support Reservation-Based Landing Pages?",
    a: "Yes. Bellhopt uses reservation context to generate a guest landing page that is timed to the stay and connected to the right property.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

const sharedCapabilities = [
  {
    number: "01",
    title: "Property Data Sync",
    desc: "Bellhopt keeps property names, addresses, coordinates, and operating details aligned with your PMS.",
  },
  {
    number: "02",
    title: "Lock And Entry Instructions",
    desc: "Arrival-critical access notes stay tied to the correct property so the team arrives with the right context.",
  },
  {
    number: "03",
    title: "New Properties Added",
    desc: "As your portfolio grows, Bellhopt can pull in newly created homes instead of relying on manual re-entry.",
  },
  {
    number: "04",
    title: "Reservation-Based Landing Pages",
    desc: "Each reservation can trigger a guest landing page with pre-arrival ordering, timing, and concierge context.",
  },
];

const workflowSteps = [
  {
    step: "Connect And Map",
    desc: "Authorize the integration, confirm the property mapping, and align the launch scope with Bellhopt.",
  },
  {
    step: "Sync Property And Reservation Data",
    desc: "Bellhopt pulls the property details, access information, and reservation timing needed to run the arrival flow accurately.",
  },
  {
    step: "Launch The Guest Experience",
    desc: "Guests receive a reservation-based landing page while Bellhopt coordinates delivery, stocking, and arrival execution.",
  },
];

const dataCategories = [
  {
    title: "Property Details",
    items: ["Property Names And Identifiers", "Addresses And Location Data", "Listing Metadata Used For Operations"],
  },
  {
    title: "Arrival Instructions",
    items: ["Lock Codes Or Access Notes", "Entry Directions", "Stay-Specific Arrival Context"],
  },
  {
    title: "Reservation Context",
    items: ["Check-In And Check-Out Timing", "Reservation Associations", "Guest Communication Timing"],
  },
  {
    title: "Launch Readiness",
    items: ["New Property Creation", "Property Updates Reflected In Bellhopt", "Consistent Setup Across The Portfolio"],
  },
];

const integrations = [
  {
    name: "Streamline",
    status: "Live",
    desc: "Connect reservation timing and property context so Bellhopt can trigger the arrival workflow without manual coordination.",
    highlight: "Best For Teams Running Bellhopt Across Multi-Property Portfolios",
    link: "/integrations/streamline",
    cta: "View Streamline Integration",
  },
  {
    name: "Hospitable",
    status: "Live",
    desc: "Sync property information, entry instructions, new listings, and reservation context to keep Bellhopt aligned with Hospitable.",
    highlight: "Best For Teams That Need Fast Property Sync And Ongoing Portfolio Updates",
    link: "/integrations/hospitable",
    cta: "View Hospitable Integration",
  },
  {
    name: "Guesty",
    status: "Available",
    desc: "Use reservation-aware guest context to power Bellhopt's pre-arrival experience and operating workflow.",
    highlight: "Great For Teams Standardizing Guest Arrival Workflows Across Markets",
    link: null,
    cta: "Contact Sales",
  },
  {
    name: "Akia",
    status: "Available",
    desc: "Align Bellhopt with guest messaging and arrival timing so the experience feels coordinated from outreach through execution.",
    highlight: "Ideal For Teams Pairing Concierge Delivery With Managed Guest Communications",
    link: null,
    cta: "Contact Sales",
  },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function FeatureCard({ number, title, desc }) {
  return (
    <div className="rounded-[28px] border border-[#E5E7EB] bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF1F1] text-[13px] font-semibold tracking-[0.08em] text-[#EF4949]">
          {number}
        </span>
        <h3 className="font-segoe text-[20px] font-semibold text-[#111827]">{title}</h3>
      </div>
      <p className="mt-4 text-[15px] leading-7 text-[#6B7280]">{desc}</p>
    </div>
  );
}

function IntegrationCard({ name, status, desc, highlight, link, cta }) {
  return (
    <div className="flex h-full flex-col rounded-[28px] border border-[#E5E7EB] bg-white/90 p-6 shadow-[0_14px_36px_rgba(15,23,42,0.05)]">
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-segoe text-[22px] font-semibold text-[#111827]">{name}</h3>
        <span className="rounded-full border border-[#FECACA] bg-[#FFF5F5] px-3 py-1 text-[11px] font-semibold tracking-[0.08em] text-[#B91C1C]">
          {status}
        </span>
      </div>
      <p className="mt-4 flex-grow text-[16px] leading-7 text-[#6B7280]">{desc}</p>
      <div className="mt-5 rounded-2xl bg-[#F9FAFB] px-4 py-3 text-[13px] font-medium leading-6 text-[#374151]">
        {highlight}
      </div>
      {link ? (
        <Link
          to={link}
          className="mt-5 text-[14px] font-semibold text-[#111827] transition-colors hover:text-[#EF4949]"
        >
          {cta} →
        </Link>
      ) : (
        <a
          href="mailto:sales@bellhopt.com"
          className="mt-5 text-[14px] font-semibold text-[#111827] transition-colors hover:text-[#EF4949]"
        >
          {cta} →
        </a>
      )}
    </div>
  );
}

function IntegrationsPage() {
  const title = "PMS Integrations | Streamline, Hospitable, Guesty, Akia | Bellhopt";
  const description =
    "Bellhopt connects to Streamline, Hospitable, Guesty, and Akia to sync property data, entry instructions, new properties, and reservation-based guest landing pages.";
  const canonical = "https://bellhopt.com/integrations/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Bellhopt Integrations",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "Integrations", item: canonical },
      ],
    },
  ];

  return (
    <SeoMarketingShell>
      <SeoHead
        title={title}
        description={description}
        canonical="/integrations/"
        robots="index,follow"
        ogTitle="Bellhopt Integrations"
        ogDescription="Bellhopt connects to your PMS and guest communication tools to sync property data and power reservation-based arrival experiences."
        structuredData={structuredData}
      />

      <main className="m-auto w-[90%] max-w-[1100px] pb-10 pt-24 mobile:w-[95%]">
        <section className={`${GLASS_CARD} relative overflow-hidden p-8 mobile:p-5`}>
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -right-24 top-0 h-64 w-64 rounded-full bg-[#FFE4E6] blur-3xl opacity-70" />
            <div className="absolute bottom-0 left-0 h-56 w-56 rounded-full bg-[#FEE2E2] blur-3xl opacity-60" />
          </div>

          <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)] lg:items-end">
            <div>
              <p className="text-[13px] font-semibold tracking-[0.14em] text-[#EF4949]">PMS Integrations</p>
              <h1 className="mt-3 font-segoe text-[44px] font-semibold leading-tight text-[#111827] mobile:text-[30px]">
                Sync Property Data And Launch Reservation-Based Guest Experiences
              </h1>
              <p className="mt-4 text-[20px] leading-8 text-[#6B7280] mobile:text-[17px]">
                Bellhopt plugs into your PMS and guest systems to keep property information, lock
                and entry instructions, new properties, and reservation timing aligned. That data
                powers a cleaner rollout, a reservation-based landing page, and a more reliable
                arrival standard.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
                  Book A Demo
                </a>
                <Link to="/integrations/hospitable" className={SECONDARY_CTA}>
                  View Hospitable Integration
                </Link>
              </div>
            </div>

            <div className="rounded-[32px] border border-white/70 bg-white/85 p-6 shadow-[0_20px_50px_rgba(15,23,42,0.08)] backdrop-blur-xl">
              <h2 className="font-segoe text-[24px] font-semibold text-[#111827]">
                What Every Integration Unlocks
              </h2>
              <div className="mt-5 space-y-4">
                {sharedCapabilities.map((capability) => (
                  <div
                    key={capability.title}
                    className="flex items-start gap-4 rounded-2xl border border-[#F1F5F9] bg-[#FCFCFD] px-4 py-4"
                  >
                    <span className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#111827] text-[11px] font-semibold tracking-[0.08em] text-white">
                      {capability.number}
                    </span>
                    <div>
                      <h3 className="font-segoe text-[17px] font-semibold text-[#111827]">
                        {capability.title}
                      </h3>
                      <p className="mt-1 text-[14px] leading-6 text-[#6B7280]">{capability.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="mt-6 space-y-6">
          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="font-segoe text-[30px] font-semibold text-[#111827] mobile:text-[24px]">
                  Why The Integration Layer Matters
                </h2>
                <p className="mt-3 max-w-[760px] text-[18px] leading-8 text-[#6B7280] mobile:text-[16px]">
                  Bellhopt is not just pulling reservation data. The integration layer keeps the
                  operating details behind the stay current so your team does not have to manage
                  duplicate property records, chase entry notes, or manually build guest arrival
                  experiences one reservation at a time.
                </p>
              </div>
              <div className="rounded-2xl border border-[#E5E7EB] bg-white/90 px-4 py-3 text-[13px] font-semibold tracking-[0.08em] text-[#374151]">
                Built For Portfolio Scale
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {sharedCapabilities.map((capability) => (
                <FeatureCard key={capability.title} {...capability} />
              ))}
            </div>
          </section>

          <Section id="workflow" title="How The Sync Works">
            <div className="grid gap-4 md:grid-cols-3">
              {workflowSteps.map((item, index) => (
                <div key={item.step} className="rounded-[28px] border border-[#E5E7EB] bg-white/90 p-5">
                  <p className="text-[12px] font-semibold tracking-[0.14em] text-[#EF4949]">
                    Step 0{index + 1}
                  </p>
                  <h3 className="mt-3 font-segoe text-[20px] font-semibold text-[#111827]">
                    {item.step}
                  </h3>
                  <p className="mt-3 text-[15px] leading-7 text-[#6B7280]">{item.desc}</p>
                </div>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe text-[30px] font-semibold text-[#111827] mobile:text-[24px]">
              Supported Integrations
            </h2>
            <p className="mt-3 max-w-[760px] text-[18px] leading-8 text-[#6B7280] mobile:text-[16px]">
              Every Bellhopt integration is built around the same operational outcome: accurate
              property sync, reservation-aware guest journeys, and fewer manual tasks for your team.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {integrations.map((integration) => (
                <IntegrationCard key={integration.name} {...integration} />
              ))}
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe text-[30px] font-semibold text-[#111827] mobile:text-[24px]">
              What Bellhopt Uses From The Integration
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {dataCategories.map((category) => (
                <div key={category.title} className="rounded-[28px] border border-[#E5E7EB] bg-white/90 p-5">
                  <h3 className="font-segoe text-[20px] font-semibold text-[#111827]">
                    {category.title}
                  </h3>
                  <ul className="mt-4 list-disc pl-6 text-[15px] leading-7 text-[#6B7280]">
                    {category.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <Section id="security" title="Secure By Design">
            <p>
              Bellhopt only uses the data required to run the arrival layer and fulfill guest
              requests correctly. Access is permissioned, operational actions are logged, and the
              guest experience is separated from internal system details.
            </p>
            <ul className="mt-4 list-disc pl-6">
              <li>Least-Privilege Access</li>
              <li>Environment-Based Configuration</li>
              <li>No Sensitive Internal Data Exposed In The Guest Flow</li>
            </ul>
          </Section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details
                  key={item.q}
                  className="rounded-2xl border border-[#E5E7EB] bg-white/90 p-4 open:bg-[#F9FAFB]"
                >
                  <summary className="cursor-pointer font-segoe text-[18px] font-semibold text-[#111827]">
                    {item.q}
                  </summary>
                  <p className="mt-3 text-[16px] leading-7 text-[#6B7280]">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe text-[30px] font-semibold text-[#111827] mobile:text-[24px]">
              Make Arrival-Ready The Standard Without Rebuilding Your Stack
            </h2>
            <p className="mt-3 text-[18px] leading-8 text-[#6B7280] mobile:text-[16px]">
              Bellhopt plugs into the systems you already use and turns synced data into a
              reservation-based guest experience your team can actually operate at scale.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
                Schedule A Demo
              </a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>
                Talk To Sales
              </a>
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe text-[30px] font-semibold text-[#111827] mobile:text-[24px]">
              Related Pages
            </h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] leading-8 text-[#6B7280] mobile:text-[16px]">
              <li>
                <Link className="text-primary" to="/integrations/streamline">
                  Streamline Integration
                </Link>
              </li>
              <li>
                <Link className="text-primary" to="/integrations/hospitable">
                  Hospitable Integration
                </Link>
              </li>
              <li>
                <Link className="text-primary" to="/for/property-managers">
                  For Property Managers
                </Link>
              </li>
              <li>
                <Link className="text-primary" to="/for/enterprise-property-managers">
                  Enterprise Property Managers
                </Link>
              </li>
              <li>
                <Link className="text-primary" to="/how-it-works">
                  How It Works
                </Link>
              </li>
              <li>
                <Link className="text-primary" to="/pricing">
                  Pricing
                </Link>
              </li>
            </ul>
          </section>
        </div>
      </main>
    </SeoMarketingShell>
  );
}

export default IntegrationsPage;
